import { TooltipWrapper } from 'components/TooltipWrapper/TooltipWrapper';
import type { BimChapterEnum, FileInput } from 'gql/graphql';
import { ProjectMethodologyEnum, ProjectStatusEnum, WorkOrderStatusEnum } from 'gql/graphql';
import { useProjectData } from 'hooks/useProjectData';
import { useSendGpnAnalyticsEvent } from 'hooks/useSendGpnAnalyticsEvent';
import type { IListItem } from 'types/Contract';
import { Button } from 'uikit';

import type React from 'react';
import { useCallback, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

import { BimUploadPriceFilesModal } from '../../BimCommonComponents/BimUploadPriceFilesModal';
import { useBimWorkOrderObjectsListItems } from '../hooks';
import { isBimChapter } from '../types';

import { useBimUploadPriceFiles } from './hooks/useBimUploadPriceFiles';

interface IBimUploadPriceFiles {
    selectedChapterOrSection?: IListItem<string>;
    selectedObject?: IListItem<number>;
    workOrderId?: number;
    onUploadSuccess: () => void;
    workOrderStatus?: WorkOrderStatusEnum;
    isFrameContract: boolean;
    togglePopover: () => void;
    isOther: boolean;
    statusProject?: ProjectStatusEnum;
}

export const BimUploadPriceFiles: React.FC<IBimUploadPriceFiles> = (props) => {
    const {
        selectedChapterOrSection,
        selectedObject,
        togglePopover,
        workOrderId,
        onUploadSuccess,
        workOrderStatus,
        statusProject,
        isFrameContract,
        isOther,
    } = props;
    const [isModalOpen, setIsModalOpen] = useState(false);

    const { id: projectId } = useParams();
    const { projectData } = useProjectData(projectId, false);

    const { sendGpnAnalyticsEvent } = useSendGpnAnalyticsEvent();

    const sendAnalyticsHandler = useCallback(
        (eventId: string) => {
            sendGpnAnalyticsEvent({
                componentId: eventId,
                projectId,
                orderId: workOrderId,
                orderStatus: workOrderStatus,
                projectName: projectData?.name,
                projectType: projectData?.contractType,
                projectDO: projectData?.subsidiary?.name,
                methodology: ProjectMethodologyEnum.Bim,
            });
        },
        [
            projectData?.contractType,
            projectData?.name,
            projectData?.subsidiary?.name,
            projectId,
            sendGpnAnalyticsEvent,
            workOrderId,
            workOrderStatus,
        ],
    );

    const {
        bimUploadPriceFilesData,
        bimUploadPriceFilesStatus,
        bimUploadPriceFilesError,
        bimUploadPriceFilesMakeRequest,
        bimUploadPriceFilesReset,
        bimUploadPriceFilesAbortRequest,
    } = useBimUploadPriceFiles(sendAnalyticsHandler);

    const onClose = useCallback(
        (isSuccess: boolean) => {
            setIsModalOpen(false);
            togglePopover();

            if (isSuccess) {
                onUploadSuccess();
            }
        },
        [onUploadSuccess, togglePopover],
    );

    const onOpen = useCallback(() => {
        setIsModalOpen(true);
        sendAnalyticsHandler('rim_estimates_add button_pressed');
    }, [sendAnalyticsHandler]);

    const bimChapter = useMemo(
        () =>
            isBimChapter(selectedChapterOrSection?.id) ? (selectedChapterOrSection?.id as BimChapterEnum) : undefined,
        [selectedChapterOrSection?.id],
    );

    const isWorkOrderStatusInProgress = workOrderStatus === WorkOrderStatusEnum.InProgress;
    const isWorkOrderStatusOnFormation = workOrderStatus === WorkOrderStatusEnum.OnFormation;
    const isProjectStatusDelayed = statusProject === ProjectStatusEnum.Delayed;

    const disabledButton = !isWorkOrderStatusOnFormation || isOther || isProjectStatusDelayed;

    const toolTipMessage = useMemo(() => {
        if (!isWorkOrderStatusInProgress || !isWorkOrderStatusOnFormation) {
            return '';
        }

        if (isWorkOrderStatusInProgress) {
            return `В ${
                isFrameContract ? 'НЗ' : 'Расчете'
            } в статусе "В работе" добавление смет доступно через создание Акта КС-2`;
        }

        return '';
    }, [isFrameContract, isWorkOrderStatusInProgress, isWorkOrderStatusOnFormation]);

    const { reset: objectsListItemsReset, data: objectsListItems } = useBimWorkOrderObjectsListItems({
        workOrderId,
        rootId: selectedObject?.id,
        requestKey: 'BimWorkOrderUploadPrice',
        withEffect: !isOther,
    });

    const successBtnTitle = useMemo(() => `Перейти в ${isFrameContract ? 'Наряд-Заказ' : 'Расчет'}`, [isFrameContract]);

    const resetObjectsListItems = useCallback(() => {
        objectsListItemsReset(undefined);
    }, [objectsListItemsReset]);

    const makeUploadRequestHandler = useCallback(
        (vars: {
            chapter: BimChapterEnum | undefined;
            filesContent: FileInput[];
            workOrderObjectId: number | undefined;
        }) => {
            const { chapter, filesContent, workOrderObjectId } = vars;

            if (chapter) {
                bimUploadPriceFilesMakeRequest({
                    chapter,
                    filesContent,
                    workOrderObjectId,
                    workOrderId,
                });
                sendAnalyticsHandler('rim_estimates_add button in modal window_pressed');
            }
        },
        [bimUploadPriceFilesMakeRequest, sendAnalyticsHandler, workOrderId],
    );

    return (
        <>
            <TooltipWrapper direction="rightCenter" message={toolTipMessage} isHidden={!toolTipMessage}>
                <Button
                    form="brick"
                    width="full"
                    label="Cмету"
                    size="s"
                    view="clear"
                    onClick={onOpen}
                    disabled={disabledButton}
                />
            </TooltipWrapper>

            {isModalOpen && (
                <BimUploadPriceFilesModal
                    selectedObject={selectedObject}
                    chapter={bimChapter}
                    onClose={onClose}
                    titleModal="Загрузка сметы"
                    textInformer="Загружаемый файл сметы в формате xml будет относиться к выбранному объекту или подобъекту"
                    uploadData={bimUploadPriceFilesData}
                    uploadStatus={bimUploadPriceFilesStatus}
                    uploadError={bimUploadPriceFilesError}
                    uploadMakeRequest={makeUploadRequestHandler}
                    uploadReset={bimUploadPriceFilesReset}
                    uploadAbort={bimUploadPriceFilesAbortRequest}
                    objectsListItems={objectsListItems}
                    successButtonTitle={successBtnTitle}
                    isConfirmDisabled={!workOrderId}
                    outerResetHandler={resetObjectsListItems}
                />
            )}
        </>
    );
};

BimUploadPriceFiles.displayName = 'BimUploadPriceFiles';
